import React, { forwardRef, useRef, useImperativeHandle } from 'react';

import { cn } from '../../utils';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  inputSize?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      options,
      placeholder,
      inputSize = 'md',
      loading = false,
      id,
      required,
      'aria-describedby': ariaDescribedBy,
      onChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    const selectRef = useRef<HTMLSelectElement>(null);
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const helperId = `${selectId}-helper`;

    // Combine external ref with internal ref
    useImperativeHandle(ref, () => selectRef.current as HTMLSelectElement);

    const hasError = Boolean(error);

    // Build aria-describedby
    const describedByIds = [];
    if (error) describedByIds.push(errorId);
    if (helperText) describedByIds.push(helperId);
    if (ariaDescribedBy) describedByIds.push(ariaDescribedBy);
    const finalAriaDescribedBy =
      describedByIds.length > 0 ? describedByIds.join(' ') : undefined;

    const sizeClasses = {
      sm: 'h-8 px-2 text-sm',
      md: 'h-10 px-3 text-sm',
      lg: 'h-12 px-4 text-base',
    };

    const baseClasses = cn(
      'w-full rounded-md border transition-colors duration-200',
      'placeholder:text-gray-400',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
      sizeClasses[inputSize],
      hasError
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 bg-white',
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'block text-sm font-medium mb-1',
              hasError ? 'text-red-700' : 'text-gray-700',
              required && "after:content-['*'] after:ml-0.5 after:text-red-500"
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={selectRef}
            id={selectId}
            className={baseClasses}
            aria-invalid={hasError}
            aria-describedby={finalAriaDescribedBy}
            aria-required={required}
            required={required}
            onChange={onChange}
            onBlur={onBlur}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          {loading && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <div
                className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
                aria-label="Loading"
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div
            id={errorId}
            className="mt-1 text-sm text-red-600"
            role="alert"
            aria-live="polite"
          >
            {error}
          </div>
        )}

        {/* Helper Text */}
        {helperText && !error && (
          <div id={helperId} className="mt-1 text-sm text-gray-500">
            {helperText}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
